/**
 * 会话历史 API 服务
 *
 * 对应后端：apps/agent-service/src/chatbi/knowledge/routers/sessions.py
 */
import apiClient from './index'
import type { ActiveTaskInfo } from '@/types/task'

// ============== 类型定义 ==============

// 会话状态
export type SessionStatus = 'active' | 'archived' | 'deleted'

// 对话状态
export type TraceStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

// 会话响应
export interface SessionResponse {
  id: string
  project_id: string
  user_id: string
  session_id: string
  title?: string
  source: string
  message_count: number
  last_message_at?: string
  /** 首条用户查询（用于推荐卡片回显） */
  first_query?: string
  /** Agent/Skill 标签列表（持久化在 PG，如 ["data_query", "chatbi"]） */
  agent_tags?: string[]
  /** 最后一条对话记录的状态（后端一次性返回，避免 N+1 查询） */
  last_trace_status?: TraceStatus
  /** 当前会话关联的活动任务（长任务模式下由后端携带，用于前端恢复任务状态） */
  active_task?: ActiveTaskInfo
  status: SessionStatus
  created_by?: string
  created_at: string
  updated_at?: string
}

// 对话记录响应
export interface TraceResponse {
  id: string
  session_id: string
  trace_id: string
  question?: string
  answer?: string
  status: TraceStatus
  duration_ms?: number
  trace_data?: Record<string, unknown>
  created_at: string
  updated_at?: string
}

// 创建会话请求
export interface SessionCreate {
  project_id: string
  user_id: string
  session_id?: string
  title?: string
  source?: string
  created_by?: string
}

// 更新会话请求
export interface SessionUpdate {
  title?: string
  status?: SessionStatus
}

// 创建对话记录请求
export interface TraceCreate {
  session_id: string
  trace_id?: string
  question?: string
  answer?: string
  status?: TraceStatus
  duration_ms?: number
  trace_data?: Record<string, unknown>
}

// 更新对话记录请求
export interface TraceUpdate {
  question?: string
  answer?: string
  status?: TraceStatus
  duration_ms?: number
  trace_data?: Record<string, unknown>
}

// 分页响应 - 统一使用 @/types 中的定义
import type { PaginatedResponse } from '@/types'

// ============== API 方法 ==============

const BASE_PATH = '/chatbi/history'

/**
 * 会话 API
 */
export const sessionsApi = {
  /**
   * 创建会话（沿用全局超时，兼容冷启动和表初始化场景）
   *
   * 超时策略：
   * - 热连接通常 < 1s，冷启动或首次建表可能明显高于 8s
   * - 会话创建已改为后台异步，不需要再人为压缩超时窗口
   */
  async create(data: SessionCreate): Promise<SessionResponse> {
    return apiClient.post<SessionResponse>(`${BASE_PATH}/sessions`, data)
  },

  /**
   * 获取会话列表
   *
   * 注意：响应拦截器处理方式不一致，需要兼容处理
   */
  async list(params: {
    project_id: string
    user_id: string
    status?: SessionStatus
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<SessionResponse>> {
    // 响应拦截器可能返回数组或 { data, total, page, page_size } 对象
    const response = await apiClient.get<PaginatedResponse<SessionResponse> | SessionResponse[]>(`${BASE_PATH}/sessions`, { params })
    
    // 兼容处理：如果返回的是数组，包装为 PaginatedResponse 格式
    if (Array.isArray(response)) {
      return {
        data: response,
        total: response.length,
        page: params.page || 1,
        page_size: params.page_size || 20
      }
    }
    
    // 如果已经是正确格式，直接返回
    return response as PaginatedResponse<SessionResponse>
  },

  /**
   * 获取单个会话
   */
  async get(sessionId: string): Promise<SessionResponse> {
    return apiClient.get<SessionResponse>(`${BASE_PATH}/sessions`, {
      params: { session_id: sessionId }
    })
  },

  /**
   * 更新会话
   */
  async update(sessionId: string, data: SessionUpdate): Promise<SessionResponse> {
    return apiClient.put<SessionResponse>(`${BASE_PATH}/sessions/${sessionId}`, data)
  },

  /**
   * 删除会话
   *
   * 后端接口：POST /sessions/delete（使用 POST 方法携带请求体）
   */
  async delete(sessionIds: string[], hard: boolean = false): Promise<void> {
    const { getProjectId } = await import('@/utils')
    await apiClient.post(`${BASE_PATH}/sessions/delete`, {
      ids: sessionIds,
      project_id: getProjectId(),
      hard
    })
  },

  /**
   * 归档会话
   */
  async archive(sessionId: string): Promise<SessionResponse> {
    return this.update(sessionId, { status: 'archived' })
  },

  /**
   * 更新会话的 Agent/Skill 标签（合并去重，持久化到 PG）
   */
  async updateAgentTags(sessionId: string, agentTags: string[], projectId: string): Promise<SessionResponse> {
    return apiClient.patch<SessionResponse>(
      `${BASE_PATH}/sessions/${sessionId}/agent-tags`,
      agentTags,
      { params: { project_id: projectId } },
    )
  }
}

/**
 * 对话记录 API
 */
export const tracesApi = {
  /**
   * 创建对话记录
   */
  async create(data: TraceCreate): Promise<TraceResponse> {
    return apiClient.post<TraceResponse>(`${BASE_PATH}/traces`, data)
  },

  /**
   * 获取对话记录列表
   *
   * 注意：响应拦截器处理方式不一致，需要兼容处理
   */
  async list(params: {
    session_id: string
    status?: TraceStatus
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<TraceResponse>> {
    // 响应拦截器可能返回数组或 { data, total, page, page_size } 对象
    const response = await apiClient.get<PaginatedResponse<TraceResponse> | TraceResponse[]>(`${BASE_PATH}/traces`, { params })
    
    // 兼容处理：如果返回的是数组，包装为 PaginatedResponse 格式
    if (Array.isArray(response)) {
      return {
        data: response,
        total: response.length,
        page: params.page || 1,
        page_size: params.page_size || 20
      }
    }
    
    // 如果已经是正确格式，直接返回
    return response as PaginatedResponse<TraceResponse>
  },

  /**
   * 获取单个对话记录
   */
  async get(traceId: string): Promise<TraceResponse> {
    return apiClient.get<TraceResponse>(`${BASE_PATH}/traces`, {
      params: { trace_id: traceId }
    })
  },

  /**
   * 更新对话记录
   */
  async update(traceId: string, data: TraceUpdate): Promise<TraceResponse> {
    return apiClient.put<TraceResponse>(`${BASE_PATH}/traces/${traceId}`, data)
  },

  /**
   * 删除对话记录
   *
   * 后端接口：POST /traces/delete（使用 POST 方法携带请求体）
   */
  async delete(traceIds: string[], hard: boolean = false): Promise<void> {
    await apiClient.post(`${BASE_PATH}/traces/delete`, {
      ids: traceIds,
      hard
    })
  }
}

// 导出默认 API
export default {
  sessions: sessionsApi,
  traces: tracesApi
}
